/**
 * AssetSelectionActions Component
 * Rename / Replace / Mark obsolete / Delete for the files an editor has selected.
 * Rename and Replace need exactly one file; the others work on any number.
 */

import React, { useRef, useState } from 'react';
import { renameAsset, replaceAsset, deleteAsset, makeAssetsCurrent } from './assetEditorApi';
import ObsoleteDialog from './ObsoleteDialog';

const AssetSelectionActions = ({ selectedFiles, folderFiles = [], onChanged }) => {
  const replaceInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [markingObsolete, setMarkingObsolete] = useState(false);
  const current = selectedFiles.filter(f => !f.obsolete);
  const obsolete = selectedFiles.filter(f => f.obsolete);
  const single = selectedFiles.length === 1 ? selectedFiles[0] : null;

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      onChanged();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRename = () => {
    const name = window.prompt('New name', single.name);
    if (!name || !name.trim() || name.trim() === single.name) return;
    run(() => renameAsset(single.id, name.trim()));
  };

  const handleReplaceChosen = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`Replace "${single.name}" with "${file.name}"? Existing links will show the new file.`)) return;
    run(() => replaceAsset(single.id, file));
  };

  const handleDelete = () => {
    const label = single ? `"${single.name}"` : `${selectedFiles.length} files`;
    if (!window.confirm(`Delete ${label} from Reseller Assets? Resellers will no longer see it.`)) return;
    run(async () => {
      for (const file of selectedFiles) {
        await deleteAsset(file.id);
      }
    });
  };

  const handleMakeCurrent = () => {
    run(() => makeAssetsCurrent(obsolete.map(f => String(f.id))));
  };

  // A replacement is any current file in this folder that is not being marked
  const selectedIds = new Set(selectedFiles.map(f => String(f.id)));
  const candidates = folderFiles.filter(f => !f.obsolete && !selectedIds.has(String(f.id)));

  return (
    <>
      {current.length > 0 && (
        <button
          className="rf-btn rf-btn-secondary rf-btn-sm"
          onClick={() => setMarkingObsolete(true)}
          disabled={busy}
          title="Keep for customers with older machines, but hide by default"
        >
          <i className="fas fa-archive"></i> Mark obsolete
        </button>
      )}
      {obsolete.length > 0 && (
        <button className="rf-btn rf-btn-secondary rf-btn-sm" onClick={handleMakeCurrent} disabled={busy}>
          <i className="fas fa-undo"></i> Make current
        </button>
      )}
      {markingObsolete && (
        <ObsoleteDialog
          files={current}
          candidates={candidates}
          onClose={() => setMarkingObsolete(false)}
          onDone={() => {
            setMarkingObsolete(false);
            onChanged();
          }}
        />
      )}
      {single && (
        <>
          <button className="rf-btn rf-btn-secondary rf-btn-sm" onClick={handleRename} disabled={busy}>
            <i className="fas fa-pen"></i> Rename
          </button>
          <button className="rf-btn rf-btn-secondary rf-btn-sm" onClick={() => replaceInputRef.current.click()} disabled={busy}>
            <i className="fas fa-sync-alt"></i> Replace
          </button>
          <input ref={replaceInputRef} type="file" hidden onChange={handleReplaceChosen} />
        </>
      )}
      <button className="rf-btn rf-btn-danger rf-btn-sm" onClick={handleDelete} disabled={busy}>
        {busy ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>} Delete
      </button>
    </>
  );
};

export default AssetSelectionActions;
